import { Link } from 'react-router-dom';
import { motion, useInView } from 'framer-motion';
import { useRef } from 'react';
import PageLayout from '@/components/feature/PageLayout';

const springTransition = { type: 'spring' as const, stiffness: 80, damping: 20 };

const sections = [
  {
    id: 'intro',
    title: 'Introduction',
    content:
      'Thoughtic Solutions ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website thoughtic.com or use our services. Please read this policy carefully. By using our website or services, you consent to the data practices described in this policy.',
  },
  {
    id: 'information-collected',
    title: '1. Information We Collect',
    content:
      'We collect information that you voluntarily provide when you fill out forms on our website, including: contact forms requesting your name, email address, company name, and project requirements; career application forms requesting your name, email, resume link, portfolio URL, and cover message; and newsletter subscription forms requesting your email address. We also automatically collect certain information when you visit our website, including your IP address, browser type, operating system, referring URLs, pages viewed, and time spent on pages through Google Analytics.',
  },
  {
    id: 'how-we-use',
    title: '2. How We Use Your Information',
    content:
      'We use the information we collect to: respond to your inquiries and provide the services you request; match you with appropriate engineering talent or project opportunities; send you relevant communications about our services, updates, and promotional materials (you may opt out at any time); improve our website, services, and user experience; analyze usage patterns to optimize our content and marketing efforts; comply with legal obligations and protect our rights; and process job applications and evaluate candidates for open positions.',
  },
  {
    id: 'sharing',
    title: '3. Data Sharing & Disclosure',
    content:
      'We do not sell, trade, or rent your personal information to third parties. We may share your information in the following circumstances: with service providers who assist us in operating our website and conducting our business (such as analytics providers, email service providers, and hosting services), subject to confidentiality agreements; when required by law, court order, or governmental regulation; to protect our rights, property, or safety, and that of our clients or the public; and in connection with a business transfer, merger, or acquisition, provided the recipient agrees to honor this privacy policy.',
  },
  {
    id: 'security',
    title: '4. Data Security',
    content:
      'We implement industry-standard security measures to protect your personal information, including: TLS 1.3 encryption for all data in transit; secure server infrastructure with access controls and monitoring; regular security assessments and vulnerability scanning; and employee training on data protection and confidentiality. However, no method of electronic storage or transmission is 100% secure. While we strive to protect your data, we cannot guarantee absolute security. For detailed information about our security practices, please visit our Trust & Security page.',
  },
  {
    id: 'cookies',
    title: '5. Cookies & Tracking Technologies',
    content:
      'Our website uses cookies and similar tracking technologies to enhance your browsing experience, analyze website traffic, and understand where our visitors come from. We use Google Analytics, which places cookies on your device to collect standard internet log information and visitor behavior patterns. You can control cookie preferences through your browser settings. Disabling cookies may affect certain features of our website. We do not respond to Do Not Track signals at this time.',
  },
  {
    id: 'rights',
    title: '6. Your Data Rights',
    content:
      'Depending on your location, you may have the following rights regarding your personal data: the right to access the personal data we hold about you; the right to request correction of inaccurate data; the right to request deletion of your data, subject to legal retention requirements; the right to restrict or object to certain processing activities; the right to data portability where technically feasible; and the right to withdraw consent at any time where processing is based on consent. To exercise any of these rights, please contact us at info@thoughtic.com. We will respond to your request within 30 days.',
  },
  {
    id: 'international',
    title: '7. International Data Transfers',
    content:
      'Thoughtic Solutions is based in India, and our website is hosted on servers that may be located in various countries. If you access our website from outside India, your information may be transferred to, stored, and processed in India or other jurisdictions where our servers or service providers are located. By using our website, you consent to such transfers. We take appropriate safeguards to ensure your data receives an adequate level of protection regardless of where it is processed.',
  },
  {
    id: 'retention',
    title: '8. Data Retention',
    content:
      'We retain your personal information only for as long as necessary to fulfill the purposes for which it was collected, including: contact form submissions are retained for up to 24 months after your last interaction with us; career application data is retained for up to 12 months for talent network consideration; analytics data is retained in accordance with Google Analytics default settings (currently 26 months); and information required for legal or contractual obligations is retained for the applicable statutory period.',
  },
  {
    id: 'children',
    title: '9. Children\'s Privacy',
    content:
      'Our website and services are not directed to individuals under the age of 16. We do not knowingly collect personal information from children. If we become aware that we have inadvertently collected personal data from a child under 16, we will take steps to delete such information promptly. If you believe a child under 16 has provided us with personal information, please contact us immediately.',
  },
  {
    id: 'third-party',
    title: '10. Third-Party Links',
    content:
      'Our website may contain links to third-party websites, including LinkedIn, Twitter, and other social media platforms. We are not responsible for the privacy practices or content of these external sites. We encourage you to review the privacy policies of any third-party websites you visit through links on our site.',
  },
  {
    id: 'changes',
    title: '11. Changes to This Policy',
    content:
      'We may update this Privacy Policy from time to time to reflect changes in our practices, legal requirements, or operational needs. We will post the updated policy on this page and update the "Last updated" date. For material changes, we will provide additional notice, such as a notification on our website. Your continued use of our website after changes are posted constitutes your acceptance of the updated policy.',
  },
  {
    id: 'contact',
    title: '12. Contact Us',
    content:
      'If you have questions, concerns, or requests regarding this Privacy Policy or our data practices, please contact us at:',
    contact: true,
  },
];

function PageHero() {
  const ref = useRef(null);
  const inView = useInView(ref, { triggerOnce: true, threshold: 0.1 });

  return (
    <div className="pt-28 pb-12 md:pt-32 md:pb-16 bg-navy-900 relative overflow-hidden" ref={ref}>
      <div className="absolute inset-0 opacity-10">
        <div className="absolute inset-0" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
        }}></div>
      </div>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, ease: 'easeOut' }}
          className="text-center"
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 border border-white/20 rounded-full mb-4 md:mb-6">
            <i className="ri-lock-line text-primary-400 w-5 h-5 flex items-center justify-center"></i>
            <span className="text-sm font-medium text-white/80">Privacy</span>
          </div>
          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-display font-bold text-white leading-tight mb-4">
            Privacy Policy
          </h1>
          {/* <p className="text-base md:text-xl text-white/70 max-w-2xl mx-auto">
            Last updated: June 23, 2026
          </p> */}
        </motion.div>
      </div>
    </div>
  );
}

export default function PrivacyPage() {
  return (
    <PageLayout
      title="Privacy Policy — Thoughtic Solutions"
      description="Read Thoughtic Solutions Privacy Policy. Learn how we collect, use, share, and protect your personal information when you visit our website or use our custom software development and IT staffing services."
      keywords="Thoughtic Solutions privacy policy, data protection, personal information, GDPR compliance, IT company privacy, software development privacy"
    >
      <PageHero />

      <section className="py-12 md:py-20 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="prose prose-lg max-w-none">
            <p className="text-gray-600 leading-relaxed mb-10">
              At Thoughtic Solutions, we take your privacy seriously. This policy describes how we handle your personal information when you interact with our website and services.
            </p>

            <div className="space-y-10">
              {sections.map((section) => (
                <div key={section.id} id={section.id}>
                  <h2 className="text-xl md:text-2xl font-bold text-navy-900 mb-4">
                    {section.title}
                  </h2>
                  <p className="text-gray-600 leading-relaxed">{section.content}</p>
                  {section.contact && (
                    <div className="mt-4 p-5 bg-gray-50 rounded-xl border border-gray-100">
                      <div className="space-y-2">
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 flex items-center justify-center bg-primary-50 rounded-lg flex-shrink-0">
                            <i className="ri-mail-line text-primary-600"></i>
                          </div>
                          <a href="mailto:info@thoughtic.com" className="text-primary-600 hover:underline text-sm font-medium">
                            info@thoughtic.com
                          </a>
                        </div>
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 flex items-center justify-center bg-primary-50 rounded-lg flex-shrink-0">
                            <i className="ri-map-pin-line text-primary-600"></i>
                          </div>
                          <span className="text-sm text-gray-600">India</span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>

            <hr className="my-10 border-gray-200" />

            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <p className="text-sm text-gray-500">
                Have questions? <Link to="/trust-security" className="text-primary-600 hover:underline font-medium">Visit our Trust & Security page</Link> to learn about our security practices.
              </p>
              <Link
                to="/terms"
                className="inline-flex items-center gap-2 text-sm font-semibold text-primary-600 hover:text-primary-700 transition-colors cursor-pointer group"
              >
                View Terms of Use
                <i className="ri-arrow-right-line group-hover:translate-x-1 transition-transform"></i>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </PageLayout>
  );
}